import React from "react";
import { useNavigate } from "react-router-dom";
import { Sparkles, ChevronRight, AlertCircle } from "lucide-react";
import type { HomeBannersState } from "../hooks/useHomeBanners";

/**
 * Render de los banners de home (prueba gratis / describir la marca). Es
 * presentacional: recibe el `state` resuelto por `useHomeBanners` (que se llama
 * UNA vez por pantalla) para no duplicar el fetch cuando se rendea en mobile y
 * desktop a la vez.
 *
 * El diseño es el mismo que tenía la pantalla clásica de selección de productos
 * (banner responsive: stack en mobile, fila en desktop).
 */
export const HomeBannersView: React.FC<{
  state: HomeBannersState;
  /** Clases extra para el contenedor (ej. `sm:hidden` / `hidden sm:block`). */
  className?: string;
  /** Quita el margen inferior del banner (para layouts en fila, no apilados). */
  noMargin?: boolean;
  /** Si true, muestra el banner de ESTADO de la suscripción (cancelación
   *  programada / pausada) con prioridad sobre los promocionales. Hoy solo se
   *  pasa en las instancias mobile. */
  showStatus?: boolean;
}> = ({ state, className, noMargin, showStatus }) => {
  const navigate = useNavigate();
  const { kind, freeImagesRemaining, isArgentina, status, cancelDate, resumeDate } =
    state;

  const mb = noMargin ? "" : "mb-4";

  // ── Banner de estado de suscripción (prioridad sobre los promocionales) ──
  // Cancelación programada (ámbar) — espeja la vista del Paywall.
  if (showStatus && status === "cancelled") {
    return (
      <div className={className}>
        <div
          className={`${mb} bg-amber-50 border border-amber-200 rounded-2xl p-4 relative sm:flex sm:items-center sm:justify-between sm:gap-3`}
        >
          <div className="pb-12 sm:pb-0">
            <div className="flex items-center gap-2 mb-1">
              <AlertCircle className="w-4 h-4 text-amber-600 shrink-0" />
              <h3 className="text-[15px] font-semibold text-brand-black/90">
                Cancelación programada
              </h3>
            </div>
            <p className="text-[15px] text-[#3C3C43]/60">
              Tu plan se cancelará el{" "}
              <strong className="font-medium text-brand-black/80">
                {cancelDate}
              </strong>
              . No se te realizará ningún cobro adicional.
            </p>
          </div>
          <button
            type="button"
            onClick={() => navigate("/subscription")}
            className="absolute bottom-4 right-4 sm:relative sm:bottom-auto sm:right-auto inline-flex items-center gap-1.5 h-10 px-4 bg-white border border-neutral-200 text-brand-black/90 rounded-full shadow-sm text-[15px] font-medium hover:bg-neutral-50 transition-colors flex-shrink-0"
          >
            Ver detalles
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    );
  }
  // Suscripción pausada (azul) — espeja la vista del Paywall.
  if (showStatus && status === "paused") {
    return (
      <div className={className}>
        <div
          className={`${mb} bg-blue-50 border border-blue-200 rounded-2xl p-4 relative sm:flex sm:items-center sm:justify-between sm:gap-3`}
        >
          <div className="pb-12 sm:pb-0">
            <div className="flex items-center gap-2 mb-1">
              <AlertCircle className="w-4 h-4 text-blue-600 shrink-0" />
              <h3 className="text-[15px] font-semibold text-brand-black/90">
                Suscripción pausada
              </h3>
            </div>
            <p className="text-[15px] text-[#3C3C43]/60">
              Tu plan se reanudará el{" "}
              <strong className="font-medium text-brand-black/80">
                {resumeDate}
              </strong>
              . No se te cobrará durante la pausa.
            </p>
          </div>
          <button
            type="button"
            onClick={() => navigate("/subscription")}
            className="absolute bottom-4 right-4 sm:relative sm:bottom-auto sm:right-auto inline-flex items-center gap-1.5 h-10 px-4 bg-white border border-neutral-200 text-brand-black/90 rounded-full shadow-sm text-[15px] font-medium hover:bg-neutral-50 transition-colors flex-shrink-0"
          >
            Ver detalles
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    );
  }

  if (kind === null) return null;

  if (kind === "upsell") {
    const lowOnImages =
      freeImagesRemaining !== null && freeImagesRemaining <= 3;
    return (
      <div className={className}>
        <div
          className={`${mb} bg-brand-primary rounded-lg p-4 relative sm:flex sm:items-center sm:justify-between sm:gap-3`}
        >
          <p
            className={`text-[15px] text-white/90 sm:pb-0 ${lowOnImages ? "mb-3 sm:mb-0" : "pb-12"}`}
          >
            {lowOnImages ? (
              <>
                <span className="font-bold uppercase">
                  ¡QUÉDATE CON NOSOTROS!
                </span>{" "}
                {isArgentina ? (
                  <>
                    Te regalamos{" "}
                    <span className="font-bold">30 imágenes gratis más.</span>
                  </>
                ) : (
                  <>
                    Te regalamos{" "}
                    <span className="font-bold">
                      50% de descuento en tu primer mes.
                    </span>
                  </>
                )}
              </>
            ) : (
              "¿Te está gustando Atlas? Suscribite a un plan y conocé todas sus funciones."
            )}
          </p>
          {/* En mobile, cuando quedan ≤3 imágenes el CTA va full-width debajo
              del texto (como en el clásico); si no, flota abajo a la derecha.
              En desktop siempre es inline a la derecha. */}
          <button
            type="button"
            onClick={() => navigate("/subscription")}
            className={`inline-flex items-center justify-center gap-2 h-10 px-4 bg-white hover:bg-white/90 text-brand-primary text-[15px] font-medium rounded-md shadow-sm transition-colors flex-shrink-0 sm:relative sm:bottom-auto sm:right-auto sm:w-auto ${
              lowOnImages ? "w-full" : "absolute bottom-4 right-4"
            }`}
          >
            {lowOnImages
              ? isArgentina
                ? "Reclamar mis imágenes gratis"
                : "Reclamar mi descuento ahora"
              : "Ver planes"}
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    );
  }

  // kind === "brand"
  return (
    <div className={className}>
      <div
        className={`${mb} bg-brand-primary/[0.06] rounded-2xl p-4 relative sm:flex sm:items-center sm:justify-between sm:gap-3`}
      >
        <div className="pb-12 sm:pb-0">
          <div className="flex items-center gap-2 mb-1">
            <Sparkles className="w-4 h-4 text-brand-primary" />
            <h3 className="text-[15px] font-semibold text-brand-black/90">
              Mejorá tus fotos
            </h3>
          </div>
          <p className="text-[15px] text-[#3C3C43]/60">
            Contale a la IA sobre tu marca para que genere fotos adaptadas a su
            estilo
          </p>
        </div>
        <button
          type="button"
          onClick={() => navigate("/redes-sociales/descripcion-brand")}
          className="absolute bottom-4 right-4 sm:relative sm:bottom-auto sm:right-auto inline-flex items-center gap-2 h-10 px-4 bg-brand-primary hover:bg-brand-primary-hover text-white rounded-full shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-primary"
        >
          Mejorar fotos
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};
